import { NotifyHelper } from 'classes/helpers/notify.helper';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import lightFormat from 'date-fns/lightFormat';
import parseISO from 'date-fns/parseISO';
import { t } from 'i18next';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { getModelKeyFromModel } from 'lib_ts/classes/ms.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import {
  MachineModelType,
  ModelStatus,
} from 'lib_ts/enums/machine-models.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IEvalModelResult } from 'lib_ts/interfaces/modelling/i-eval-models';
import { IGatherShotDataQuery } from 'lib_ts/interfaces/modelling/i-gather-shot-data';
import { IMachineModel } from 'lib_ts/interfaces/modelling/i-machine-model';
import { ITrainModelsRequest } from 'lib_ts/interfaces/modelling/i-train-model';
import { clone } from 'lodash';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AdminMachineModelsService } from 'services/admin/machine-models.service';
import { useAuthStore } from 'stores/use-auth-store';

const CONTEXT_NAME = 'AdminMachineModelsContext';

interface IOptionsDict {
  names: IOption[];
  machineIDs: string[];
  types: MachineModelType[];
  statuses: ModelStatus[];
  ballTypes: BallType[];
  _created: string[];
}

export interface IMachineModelsContext {
  models: IMachineModel[];
  loading: boolean;

  options: IOptionsDict;

  /** changes lastFetched date which auto-triggers data to be fetched */
  readonly refresh: () => void;

  readonly activateModel: (config: {
    modelID: string;
    machineID: string;
  }) => Promise<boolean>;

  readonly trainModels: (
    payload: ITrainModelsRequest
  ) => Promise<IEvalModelResult[] | undefined>;

  readonly updateModel: (
    id: string,
    payload: Partial<IMachineModel>
  ) => Promise<boolean>;

  readonly archiveModels: (ids: string[]) => Promise<boolean>;

  readonly evalModelMetrics: (
    modelIDs: string[],
    query: IGatherShotDataQuery
  ) => Promise<IEvalModelResult[]>;

  /** mostly for using an existing model for a new machine */
  readonly copyModel: (payload: Partial<IMachineModel>) => Promise<boolean>;
}

const DEFAULT: IMachineModelsContext = {
  models: [],

  options: {
    names: [],
    machineIDs: [],
    statuses: [],
    ballTypes: [],
    types: [],
    _created: [],
  },

  loading: false,

  refresh: () => console.error(`${CONTEXT_NAME}: not init`),

  activateModel: async () => new Promise(() => false),

  trainModels: async () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),
  updateModel: async () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),
  archiveModels: async () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),

  evalModelMetrics: async () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),

  copyModel: async () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),
};

const getOptions = (data: IMachineModel[]): IOptionsDict => {
  try {
    if (!data) {
      throw new Error('MODELS CONTEXT: no data provided to getOptions');
    }

    return {
      names: data.map((m) => ({
        label: m.name,
        group: m.machineID,
        value: m.name,
      })),

      machineIDs: ArrayHelper.unique(data.map((m) => m.machineID)),

      statuses: ArrayHelper.unique(data.map((m) => m.status as ModelStatus)),
      ballTypes: ArrayHelper.unique(data.map((m) => m.ball_type as BallType)),

      types: ArrayHelper.unique(data.map((m) => m.type)),

      /** for some reason, some models exist without _created date */
      _created: ArrayHelper.unique(
        data.map((m) =>
          m._created ? lightFormat(parseISO(m._created), LOCAL_DATE_FORMAT) : ''
        )
      ),
    };
  } catch (e) {
    console.error(e);
    return DEFAULT.options;
  }
};

export const MachineModelsContext = createContext(DEFAULT);

interface IProps {
  children: ReactNode;
}

export const MachineModelsProvider: FC<IProps> = (props) => {
  const current = useAuthStore((s) => s.current);

  const machines = useMachinesPortalStore((s) => s.machines);
  const updateMachines = useMachinesPortalStore((s) => s.updateMachines);

  const [lastFetched, setLastFetched] = useState<Date>();

  const [models, setModels] = useState(DEFAULT.models);

  const [loading, setLoading] = useState(DEFAULT.loading);

  const options = useMemo(() => getOptions(models), [models]);

  const updateModel = useCallback(
    async (id: string, payload: Partial<IMachineModel>): Promise<boolean> => {
      try {
        setLoading(true);

        const result =
          await AdminMachineModelsService.getInstance().updateModel(
            id,
            payload
          );

        if (!result) {
          throw new Error('Empty update result');
        }

        const nextModels = clone(models);

        const index = nextModels.findIndex((v) => v._id === result._id);

        if (index !== -1) {
          if (result.archived) {
            /** remove current without replacement */
            nextModels.splice(index, 1);
          } else {
            /** remove current with replacement using updated value */
            nextModels.splice(index, 1, result);
          }
        } else if (!result.archived) {
          /** append to end */
          nextModels.push(result);
        }

        setModels(nextModels);

        return true;
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });

        return false;
      } finally {
        setLoading(false);
      }
    },
    [models]
  );

  const state: IMachineModelsContext = {
    models: models,

    options: options,

    loading: loading,

    refresh: () => setLastFetched(new Date()),

    activateModel: async (config) => {
      const model = models.find((m) => m._id === config.modelID);

      if (!model) {
        NotifyHelper.warning({
          message_md: `Failed to find model ${config.modelID} in loaded models.`,
        });
        return false;
      }

      const machine = machines.find((m) => m.machineID === config.machineID);

      if (!machine) {
        NotifyHelper.warning({
          message_md: `Failed to find machine ${config.machineID} in loaded machines.`,
        });
        return false;
      }

      if (!machine.model_ids) {
        machine.model_ids = {};
      }

      machine.model_ids[getModelKeyFromModel(model)] = model._id;

      const success = !!(await updateMachines([
        {
          _id: machine._id,
          model_ids: machine.model_ids,
        },
      ]));

      if (!success) {
        return false;
      }

      await updateModel(model._id, {
        status: ModelStatus.Published,
      });

      return true;
    },

    copyModel: async (model) => {
      try {
        setLoading(true);

        const result =
          await AdminMachineModelsService.getInstance().copyModel(model);

        if (result) {
          setModels((prev) => [...prev, result]);
        }

        return !!result;
      } catch (e) {
        console.error(e);

        return false;
      } finally {
        setLoading(false);
      }
    },

    trainModels: async (payload) => {
      try {
        setLoading(true);

        const results =
          await AdminMachineModelsService.getInstance().trainModels(payload);

        if (results && results.length > 0) {
          setLastFetched(new Date());
        }

        return results;
      } catch (e) {
        console.error(e);

        return undefined;
      } finally {
        setLoading(false);
      }
    },

    updateModel: updateModel,

    archiveModels: async (ids) => {
      try {
        setLoading(true);

        const success =
          await AdminMachineModelsService.getInstance().archiveModels(ids);

        if (success) {
          setModels((prev) => prev.filter((m) => !ids.includes(m._id)));
        }

        return success;
      } catch (e) {
        console.error(e);

        return false;
      } finally {
        setLoading(false);
      }
    },

    evalModelMetrics: async (modelIDs, query) => {
      try {
        setLoading(true);

        const result =
          await AdminMachineModelsService.getInstance().evalModelMetrics(
            modelIDs,
            query
          );

        return result;
      } catch (e) {
        console.error(e);
        return [];
      } finally {
        setLoading(false);
      }
    },
  };

  /** fetch the data whenever lastFetched changes */
  useEffect(() => {
    const fetchAllModels = async () => {
      setLoading(true);

      try {
        const allModels =
          await AdminMachineModelsService.getInstance().getAllModels();
        setModels(
          allModels.sort((a, b) => StringHelper.safeSort(a.name, b.name))
        );
      } finally {
        setLoading(false);
      }
    };

    if (current.role !== UserRole.admin) {
      return;
    }

    fetchAllModels();
  }, [lastFetched, current.role]);

  /** reload data to match session access */
  useEffect(() => {
    /** trigger refresh */
    setLastFetched((prev) => {
      if (!prev || !current.session) {
        return;
      }
      return new Date();
    });
  }, [current.session]);

  return (
    <MachineModelsContext.Provider value={state}>
      {props.children}
    </MachineModelsContext.Provider>
  );
};
